const isRangeFilter = (type) => type === 'numberRange' || type === 'dateRange';
const isMultiFilter = (type) => type === 'multiSelect';

// A text box is the right control for a column of free text, and the wrong one for a
// column of repeating values: you cannot see what is in there without typing, and a
// typo silently returns nothing. Any column whose values form a listable set becomes a
// multi-select instead. The ceiling is generous because the useful signal is "is this
// a set the reader could pick from", not "is it small" -- an id column in a 1000-row
// drilldown has 1000 distinct values and stays a text search, which is correct.
const MULTI_SELECT_MAX_OPTIONS = 200;
const emptyRangeFilter = () => ({ min: '', max: '', from: '', to: '' });

// Labels the options of the filter dropdowns: the select a metric pre-filter
// becomes, and the multi-select a listable text column becomes. Only the LABEL
// goes through here; an option's value stays the raw stringified row value the
// filter predicate matches on. BlinkTable formats the cells itself, except a
// boolean's, which it would print as the raw literal: the BlinkTable adapter
// (drilldown-blink-table.js) labels those through here, so a cell reads like
// the option that selects it.
//
// Booleans would otherwise fall through to String(v) and label the options with
// the raw JS literals, untranslated. Pipedrive's activity drilldowns select the
// BOOL `done` (Pipedrive.php:131,156) and map it through `drilldown_params`, so a
// boolean pre-filter is a real case. Scan for BOOL columns, not for boolean
// literals in the drilldown SQL, which never matches a plain reference.
//
// BigQuery NUMERIC columns arrive as floats, and String(12.0) is "12", so a money
// column's options would read "12" beside "12.5" beside "12.34". `decimals` is the
// precision hint DrilldownService attaches to NUMERIC and nothing else; every other
// column carries null and is printed unchanged.
//
// `toFixed`, not `toLocaleString`: no thousands separators, deliberately. The tile
// is the formatted surface; the drilldown is the data surface, and people copy rows
// out of it into a spreadsheet, where "1,284.50" pastes as text and stops being a
// number. BlinkTable's cell formatter (standalone/entry.table.tsx in the vendored
// Superset) keeps the same rule and points back here for the reason, so an option
// reads like the cells it selects. The cost is that the drilldown reads 1284.50
// beneath a tile reading 1,284.50, accepted in that direction, not overlooked.
//
// BIGNUMERIC deliberately does NOT arrive as a float and carries no hint: it holds
// up to 76 significant digits, so casting it would silently round a wide value on
// the way here. It reaches this renderer as an exact string and is printed as-is.
export function renderValue(v, col, strings) {
  if (v == null) return '';
  if (typeof v === 'boolean') return v ? strings.yes : strings.no;
  if (col?.decimals != null && typeof v === 'number' && Number.isFinite(v)) {
    return v.toFixed(col.decimals);
  }
  return String(v);
}

export default function DrilldownTable(userOptions = {}) {
  const options = userOptions !== null && typeof userOptions === 'object' && !Array.isArray(userOptions)
    ? userOptions
    : {};

  const strings = Object.assign({
    filterPlaceholder: 'Filter...',
    yes:               'Yes',
    no:                'No',
  }, options.strings || {});

  const initialFilters = Array.isArray(options.initialFilters) ? options.initialFilters : [];
  const rows           = Array.isArray(options.rows) ? options.rows : [];
  const columns        = Array.isArray(options.columns) ? options.columns : [];

  // Pre-process columns BEFORE Alpine takes over: upgrade columns that have
  // initial metric param filters to select dropdowns and record preset values.
  // This must happen here (not in init()) because Alpine renders the DOM before
  // calling init(), and x-model on a newly-created <select> would overwrite
  // the state value with the empty first option.
  const presetFilterValues = {};
  const multiValueFilters  = [];
  const appliedFilterLabels = [];
  const paramLabelByColumn = {};

  for (const filter of initialFilters) {
    const col = columns.find(c => c.key === filter.column);
    if (!col?.filter) continue;

    const rawVals = [...new Set(
      rows.map(r => r?.[col.key]).filter(v => v != null)
    )];

    // Option VALUES stay raw-stringified: the select comparison in _computeRows,
    // `presetFilterValues` and `multiValueFilters` all match String(rowValue), and
    // a preset arrives as the metric param's own raw value (Pipedrive maps
    // `activity_status` -> the BOOL `done` through a value_map). Only the LABEL is
    // rendered, so a boolean column offers Yes/No while still selecting on `true`.
    col.filter = {
      type: 'select',
      options: rawVals.map(String).sort(),
      optionLabels: Object.fromEntries(
        rawVals.map(v => [String(v), renderValue(v, col, strings)])
      ),
    };
    appliedFilterLabels.push({ label: filter.label, column: filter.column });

    // Track the metric param label per column so the UI can show an info tooltip
    // when the column label differs from the param label (e.g., column "Owner Id"
    // filtered by the "User" metric param).
    if (filter.paramLabel) {
      paramLabelByColumn[filter.column] = filter.paramLabel;
    }

    if (filter.values.length === 1) {
      presetFilterValues[filter.column] = String(filter.values[0]);
    } else {
      // Multi-value: can't pre-select in a single <select>, handled in _computeRows.
      multiValueFilters.push(filter);
    }
  }

  // Upgrade plain text filters to multi-selects where the column's values form a
  // listable set. Skipped for any column already carrying a metric pre-filter: those
  // own their own select semantics (including the uncloseable multi-value ones) and
  // must not be re-typed underneath that contract.
  const presetColumns = new Set(initialFilters.map(f => f.column));
  for (const col of columns) {
    if (!col?.filter || col.filter.type !== 'text' || presetColumns.has(col.key)) continue;

    const rawVals = [...new Set(rows.map(r => r?.[col.key]).filter(v => v != null && v !== ''))];
    // One option is still worth offering: a column where every row reads "succeeded"
    // tells the reader that at a glance, which an empty text box does not. Zero means
    // the column is empty in this result, and there is nothing to pick.
    if (rawVals.length < 1 || rawVals.length > MULTI_SELECT_MAX_OPTIONS) continue;

    // A column of opaque machine tokens that never repeat is a lookup, not a category:
    // a dropdown of distinct `pi_3Sk5CCGZBr...` strings is harder to use than typing
    // part of one. Both halves of the test are load-bearing. Shape alone would reject
    // `product_id`, whose values are tokens but repeat across rows, so it is a real
    // category; uniqueness alone would reject `customer_name` in any result where every
    // customer happens to appear once, which is exactly the column this feature is for.
    // Long, unspaced, and either mixed-case or digit-bearing. Requiring a digit was too
    // strict: Stripe ids are base62, so `cus_RgoOmYivhVPypq` has none, and one such value
    // in a column was enough to make the whole column read as human text -- which is how
    // customer_id ended up a dropdown on one metric and a text box on another.
    const looksLikeToken = value => {
      const text = String(value);
      if (text.length < 12 || /\s/.test(text)) return false;
      return /\d/.test(text) || (/[a-z]/.test(text) && /[A-Z]/.test(text));
    };
    const isOpaqueLookup =
      rawVals.length === rows.length && rawVals.every(looksLikeToken);
    if (isOpaqueLookup) continue;

    col.filter = {
      type: 'multiSelect',
      // Values stay raw-stringified to match the row comparison; only labels render.
      options: rawVals.map(String).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
      optionLabels: Object.fromEntries(
        rawVals.map(v => [String(v), renderValue(v, col, strings)])
      ),
    };
  }

  // Build initial filter state with all keys pre-populated BEFORE the return
  // object. This ensures Alpine's proxy tracks these keys from the start, so
  // x-model on <select> elements renders the correct selected option.
  const filterState = {};
  for (const c of columns) {
    if (!c?.filter) continue;
    if (isRangeFilter(c.filter.type)) {
      filterState[c.key] = emptyRangeFilter();
    } else if (isMultiFilter(c.filter.type)) {
      filterState[c.key] = [];
    } else {
      filterState[c.key] = presetFilterValues[c.key] || '';
    }
  }

  return {
    strings,
    rows,
    columns,
    appliedFilterLabels,
    paramLabelByColumn,
    hoveredColumn: null,
    filtersExpanded: false,
    _processedRows: [],

    state: {
      filters: filterState,
    },

    initFilters() {
      for (const c of this.columns) {
        if (!c?.filter) continue;
        if (isRangeFilter(c.filter.type)) {
          this.state.filters[c.key] = emptyRangeFilter();
        } else if (isMultiFilter(c.filter.type)) {
          this.state.filters[c.key] = [];
        } else {
          this.state.filters[c.key] = presetFilterValues[c.key] || '';
        }
      }
    },

    init() {
      this.initFilters();

      const recompute = () => { this._processedRows = this._computeRows(); };

      for (const c of this.columns) {
        if (!c?.filter) continue;
        // Ranges and multi-selects both hold a compound value, so they need a deep watch:
        // a shallow one only fires when the reference changes, and a selection toggled in
        // place would leave the table showing the previous rows.
        const isCompound = isRangeFilter(c.filter.type) || isMultiFilter(c.filter.type);
        if (isCompound) {
          this.$watch(`state.filters.${c.key}`, recompute, { deep: true });
        } else {
          this.$watch(`state.filters.${c.key}`, recompute);
        }
      }

      this.$watch('rows', recompute);

      this._processedRows = this._computeRows();
    },

    normalize(v) {
      return (v ?? '').toString().toLowerCase().trim();
    },

    ensureRangeObj(key) {
      if (!this.state.filters[key] || typeof this.state.filters[key] !== 'object') {
        this.state.filters[key] = emptyRangeFilter();
      }
    },

    processedRows() {
      return this._processedRows;
    },

    // The rows the filters keep. BlinkTable searches, sorts and pages within them.
    _computeRows() {
      return this.rows.filter(row => {
        if (!row) return false;

        // Apply multi-value initial filters that can't be represented in a single select.
        for (const filter of multiValueFilters) {
          const rowVal = row[filter.column];
          const match = filter.values.some(v => String(v) === String(rowVal));
          if (!match) return false;
        }

        for (const col of this.columns) {
          if (!col?.filter) continue;

          const key = col.key;
          const f = this.state.filters[key];

          if (col.filter.type === 'text') {
            const q = this.normalize(f);
            if (q && !this.normalize(row?.[key]).includes(q)) return false;
          }

          if (col.filter.type === 'multiSelect') {
            // Empty selection means "no filter", the same as a blank text box.
            if (Array.isArray(f) && f.length && !f.includes(String(row?.[key] ?? ''))) return false;
          }

          if (col.filter.type === 'select') {
            if (f && String(row?.[key] ?? '') !== String(f)) return false;
          }

          if (col.filter.type === 'boolean') {
            if (f !== '') {
              const v = row?.[key];
              const isTruthy = v === true || v === 1 || v === '1' || v === 'true';
              if (f === '1' && !isTruthy) return false;
              if (f === '0' && isTruthy) return false;
            }
          }

          if (col.filter.type === 'numberRange') {
            const min = f?.min !== '' ? Number(f.min) : null;
            const max = f?.max !== '' ? Number(f.max) : null;
            const v = Number(row?.[key]);
            if (min != null && v < min) return false;
            if (max != null && v > max) return false;
          }

          if (col.filter.type === 'dateRange') {
            const from = f?.from ? new Date(f.from).getTime() : null;
            const to   = f?.to   ? new Date(f.to).getTime()   : null;
            const v    = row?.[key] ? new Date(row[key]).getTime() : null;
            if (from != null && (v == null || v < from)) return false;
            if (to   != null && (v == null || v > to))   return false;
          }
        }

        return true;
      });
    },

    resetAll() {
      this.initFilters();
    },

    hasAnyFilters() {
        return Array.isArray(this.columns) && this.columns.some(c => !!c?.filter);
    },

    highlightFilter(columnKey) {
      this.hoveredColumn = columnKey;
      const el = document.getElementById('filter-' + columnKey);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
      if (!inView && typeof el.scrollIntoView === 'function') {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
      }
    },

    focusFilter(columnKey) {
      this.filtersExpanded = true;
      this.$nextTick(() => {
        this.highlightFilter(columnKey);
        const el = document.getElementById('filter-' + columnKey);
        if (el && typeof el.focus === 'function') el.focus({ preventScroll: true });
      });
    },

    activeFilterCount() {
      let count = 0;
      for (const col of this.columns) {
        if (!col?.filter) continue;
        const f = this.state.filters[col.key];
        if (f == null) continue;
        // Arrays first: a multi-select's value is an array, and `typeof [] === 'object'`,
        // so the range branch below would read f.min as undefined, compare it against ''
        // and count every multi-select column as filtered whether or not anything is
        // selected. That is what put a "+6" badge on an unfiltered table.
        if (Array.isArray(f)) {
          if (f.length) count++;
        } else if (typeof f === 'object') {
          if (f.min !== '' || f.max !== '' || f.from !== '' || f.to !== '') count++;
        } else if (f !== '') {
          count++;
        }
      }
      return count;
    },

    clearFilterHighlight() {
      this.hoveredColumn = null;
    },
  };
}
